/**
 * Lobby "How to Play" panel — bottom half of the left column, pairs with the
 * top-10 leaderboard above it. Pure-DOM, zero-state component that renders a
 * numbered list of the viewer-facing game flow so new viewers know what to
 * type the moment they land on the stream.
 *
 * Visual language matches lobby-leaderboard.ts: same glass-panel background,
 * Oswald title, Inter body. Theme vars (--sg-primary, --sg-secondary) drive
 * the accent colors so the streamer's picker applies.
 */

export interface LobbyHowToHandle {
  el: HTMLDivElement;
  dispose: () => void;
}

const STYLES = `
  .sg-howto {
    position: relative;
    /* Auto-size to header + body so the parent left column can space-evenly
       distribute the three vertical gaps. Previously height 100% forced
       the panel to fill its flex track and produced empty interior padding. */
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.75) 100%);
    border: 2px solid var(--sg-secondary, #3DEFFF);
    border-radius: 14px;
    box-shadow: 0 0 30px rgba(61,239,255,0.15), 0 4px 30px rgba(0,0,0,0.4);
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    overflow: hidden;
    pointer-events: auto;
  }
  .sg-howto-title {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.22em;
    text-align: center;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 14px rgba(255,196,61,0.4);
    padding: 10px 8px 8px 8px;
    border-bottom: 1px solid rgba(61,239,255,0.35);
    flex: 0 0 auto;
  }
  .sg-howto-body {
    /* flex 0 0 auto keeps the body exactly as tall as its numbered list;
       no flex: 1 stretch or justify-content: center filler that used to
       produce internal empty space when the parent was height-constrained. */
    flex: 0 0 auto;
    overflow: hidden;
    padding: 10px 14px 12px 14px;
  }
  .sg-howto-list {
    list-style: none;
    margin: 0;
    padding: 0;
    counter-reset: sg-howto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .sg-howto-item {
    counter-increment: sg-howto;
    position: relative;
    padding: 4px 0 4px 34px;
    font-size: 0.95rem;
    line-height: 1.45;
    color: #E2E8F0;
  }
  .sg-howto-item::before {
    content: counter(sg-howto);
    position: absolute;
    left: 0;
    top: 2px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(255,196,61,0.18);
    border: 1px solid var(--sg-primary, #FFC43D);
    color: var(--sg-primary, #FFC43D);
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-weight: 700;
    font-size: 0.85rem;
    text-align: center;
    line-height: 20px;
  }
  .sg-howto-item code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.12);
    padding: 1px 6px;
    border-radius: 4px;
    font-family: ui-monospace, monospace;
    font-size: 0.9em;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('sg-howto-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-howto-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createLobbyHowTo(mount: HTMLElement): LobbyHowToHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'sg-howto';
  el.setAttribute('data-role', 'lobby-howto');
  el.innerHTML = `
    <div class="sg-howto-title">HOW TO PLAY</div>
    <div class="sg-howto-body">
      <ol class="sg-howto-list">
        <li class="sg-howto-item">Streamer types <code>!start</code> to begin</li>
        <li class="sg-howto-item">Vote category: type <code>1</code>-<code>5</code> in chat</li>
        <li class="sg-howto-item">Pick a value: type <code>1</code>-<code>5</code> or the amount (e.g. <code>600</code>)</li>
        <li class="sg-howto-item">Clue appears - type your answer (fastest correct wins)</li>
        <li class="sg-howto-item">Top scores show in the leaderboard above</li>
      </ol>
    </div>
  `;
  mount.appendChild(el);

  return {
    el,
    dispose() {
      el.remove();
    },
  };
}
